import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Business as BusinessIcon, Recommend as RecommendIcon, Info as InfoIcon } from '@mui/icons-material';
import { authService } from '../services/authService';
import { adInsightsService } from '../services/adInsightsService';
import { useNavigate } from 'react-router-dom';
import { tokens } from '../theme/tokens';

const AdvertisersPage = () => {
  const navigate = useNavigate();
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const userData = await authService.getCurrentUser();
        const username = userData?.username || userData?.facebook_name || userData?.reddit_username;
        const userId = userData?.id;
        if (!username && !userId) { setError('Username not found. Please log in again.'); return; }
        const result = await adInsightsService.getAdInsights({ username, userId });
        if (result.status === 'success') {
          setInsights(result.data);
        } else {
          setError(result.message || 'Failed to load advertisers');
        }
      } catch (err) {
        console.error('Load advertisers error:', err);
        if (err.response?.status === 401 || err.response?.status === 403) navigate('/login');
        else setError('Failed to load advertisers data.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [navigate]);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 6, textAlign: 'center' }}>
        <CircularProgress size={48} sx={{ color: 'primary.main' }} />
        <Typography variant="h6" sx={{ mt: 3, color: 'text.secondary' }}>Loading advertisers...</Typography>
      </Container>
    );
  }

  const advertisers = insights?.advertisers || [];
  const fbAct = insights?.fb_advertisers_using_activity || [];
  const fbInt = insights?.fb_advertisers_interacted || [];
  const hasData = advertisers.length > 0 || fbAct.length > 0 || fbInt.length > 0;

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 }, pb: 6 }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 3.5 },
          mb: 3,
          borderRadius: `${tokens.radius.card}px`,
          background: tokens.hero.gradient,
          border: tokens.hero.border,
          boxShadow: tokens.hero.shadow,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1.5}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.1)', display: 'flex', border: `1px solid ${tokens.border.subtle}` }}>
              <BusinessIcon sx={{ color: tokens.text.primary, fontSize: 26 }} />
            </Box>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: tokens.text.primary, letterSpacing: '-0.02em' }}>
                Advertisers
              </Typography>
              <Typography variant="body2" sx={{ color: tokens.text.secondary, mt: 0.25 }}>
                Companies that have your contact info or have tracked your activity
              </Typography>
            </Box>
          </Stack>
          <Button
            variant="contained"
            size="small"
            onClick={() => navigate('/dashboard')}
            sx={{ bgcolor: tokens.accent.brandBlue, color: '#fff', fontWeight: 600, '&:hover': { bgcolor: '#1D4ED8' } }}
          >
            Back to Dashboard
          </Button>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {!hasData && !error && (
        <Paper elevation={0} sx={{ p: 4, textAlign: 'center', borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <InfoIcon sx={{ fontSize: 54, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom sx={{ color: 'text.primary' }}>No advertiser data found</Typography>
          <Typography variant="body2" color="text.secondary">
            Upload your Facebook DYI ZIP to see which advertisers have your information.
          </Typography>
        </Paper>
      )}

      {hasData && (
        <>
          {/* Stats cards */}
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            {advertisers.length > 0 && (
              <Grid item xs={12} sm={6} md={4}>
                <Card>
                  <CardContent>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: 'rgba(245,158,11,0.14)', display: 'flex' }}>
                        <BusinessIcon sx={{ color: '#F59E0B', fontSize: 24 }} />
                      </Box>
                      <Box>
                        <Typography variant="h3" sx={{ color: '#F59E0B', fontWeight: 800 }}>{advertisers.length}</Typography>
                        <Typography variant="body2" color="text.secondary">Have your contact info</Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            )}
            {fbAct.length > 0 && (
              <Grid item xs={12} sm={6} md={4}>
                <Card>
                  <CardContent>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: 'rgba(239,68,68,0.14)', display: 'flex' }}>
                        <BusinessIcon sx={{ color: '#EF4444', fontSize: 24 }} />
                      </Box>
                      <Box>
                        <Typography variant="h3" sx={{ color: '#EF4444', fontWeight: 800 }}>{fbAct.length}</Typography>
                        <Typography variant="body2" color="text.secondary">Using your activity</Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            )}
            {fbInt.length > 0 && (
              <Grid item xs={12} sm={6} md={4}>
                <Card>
                  <CardContent>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: 'rgba(59,130,246,0.14)', display: 'flex' }}>
                        <RecommendIcon sx={{ color: '#3B82F6', fontSize: 24 }} />
                      </Box>
                      <Box>
                        <Typography variant="h3" sx={{ color: '#3B82F6', fontWeight: 800 }}>{fbInt.length}</Typography>
                        <Typography variant="body2" color="text.secondary">You've interacted with</Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>

          {advertisers.length > 0 && (
            <Paper elevation={0} sx={{ mb: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ p: 2.5, borderBottom: 1, borderColor: 'divider' }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 700 }}>Advertisers Who Have Your Contact Info</Typography>
                  <Chip label={advertisers.length} color="warning" size="small" />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  These companies uploaded your phone number or email to Facebook to target you with ads.
                </Typography>
              </Box>
              <TableContainer sx={{ maxHeight: 360 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Advertiser Name</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {advertisers.map((name, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>{name}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {fbAct.length > 0 && (
            <Paper elevation={0} sx={{ mb: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ p: 2.5, borderBottom: 1, borderColor: 'divider' }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 700 }}>Advertisers Using Your Activity</Typography>
                  <Chip label={fbAct.length} color="warning" size="small" />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Advertisers that received or used activity associated with your account.
                </Typography>
              </Box>
              <TableContainer sx={{ maxHeight: 320 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Advertiser</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fbAct.map((row, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>{row}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {fbInt.length > 0 && (
            <Paper elevation={0} sx={{ mb: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ p: 2.5, borderBottom: 1, borderColor: 'divider' }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 700 }}>Advertisers You&apos;ve Interacted With</Typography>
                  <Chip label={fbInt.length} color="info" size="small" />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Brands you engaged with that may influence future ads.
                </Typography>
              </Box>
              <TableContainer sx={{ maxHeight: 320 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Advertiser</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fbInt.map((row, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>{row}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </>
      )}
    </Container>
  );
};

export default AdvertisersPage;
